import uniqueId from 'lodash.uniqueid';
import {
  PORTAL_WWW, PORTAL_HOST, PORTAL_IMAGES_ENDPOINT, rootSubwebs, rootSubwebsHomeSlug
} from 'services/constants';
import {
  formatDistanceToNow, isValid, format, set
} from 'date-fns'
import { pt } from 'date-fns/locale'

export const generateKey = prefix => `${uniqueId(prefix)}_${new Date().getTime()}`;

export const generatePortalImageSrc = (src) => {
  // A external image
  if (
    (src !== undefined && src !== null && src.includes('http'))
    || src.includes('data:image')
  ) {
    return src;
  }

  return `${PORTAL_WWW}/${encodeURIComponent(src)}`;
};

export const generatePortalImg = ({
  src,
  isLocal = true,
  height = 100,
  width,
}) => {
  const baseUrl = `${PORTAL_IMAGES_ENDPOINT}/image/resizer?imageUrl=`;
  const url = isLocal
    ? `${baseUrl}${PORTAL_WWW}/${src}`
    : `${baseUrl}${src}`;
  const urlResized = width
    ? `${url}&width=${width}`
    : `${url}&height=${height}`;
  return urlResized;
};

export const generateEditorPath = ({
  lang = 'pt',
  subweb,
  pageId,
  moduleId,
  removePre = false,
  next = '',
}) => {
  const isModule = pageId && moduleId;
  const pre = `/${subweb}/editor/${lang}`;

  let path = `/page/${encodeURIComponent(pageId)}`;
  if (isModule) path = `/page/${encodeURIComponent(pageId)}/module/${moduleId}`;
  if (!removePre) path = pre + path;
  if (next) path += next;
  return path;
};

// mainly to check if user is in editor or redirect to homepage
export const computePathEditor = gatherRedirect => {
  const splitUrl = gatherRedirect.split('/');
  const { length } = splitUrl;

  // when login redirect to home page
  const { pathname } = new URL(gatherRedirect, 'http://base.url');
  const isLogin = pathname === '/login';
  if (isLogin) return '/';

  // when in editor encode the pageId
  // this is unnecessary now...
  // was used when the pages were not number ids but actually complex names
  const isEditor = splitUrl.includes('editor') && splitUrl.includes('page');
  let pathToRedirect = gatherRedirect;
  const dontEncode = [
    'nav',
    'add',
    'module',
    'layout',
    'structure',
    'permissions',
    'diff',
    'json',
    'history',
    'permissions',
  ];
  if (isEditor) {
    const pageIndex = splitUrl.findIndex(splitted => splitted === 'page');
    const dontEncodeIndex = splitUrl.findIndex(splitted => dontEncode.includes(splitted)
    );
    const hasReservedPath = dontEncodeIndex !== -1;
    const pre = `${splitUrl.slice(0, pageIndex + 1).join('/')}/`;

    if (hasReservedPath) {
      const after = `/${splitUrl.slice(dontEncodeIndex, length).join('/')}`;
      const pageId = splitUrl.slice(pageIndex + 1, dontEncodeIndex).join('/');
      const pageIdEncoded = encodeURIComponent(pageId);
      pathToRedirect = pre + pageIdEncoded + after;
      return pathToRedirect;
    }

    const after = splitUrl.slice(pageIndex + 1, length).join('/');
    // preserve query
    const afterQuerySplit = after.split('?')
    const hasQuery = afterQuerySplit.length > 1
    const pageId = afterQuerySplit[0]
    const query = hasQuery ? `?${afterQuerySplit[1]}` : ''

    const pageIdEncoded = encodeURIComponent(pageId);

    pathToRedirect = pre + pageIdEncoded + query;
  }
  return pathToRedirect;
};

export const generateSubwebPath = ({
  subweb, lang = 'pt', slug, withDomain = false, withHost = false, withRootHome = false,
}) => {
  const isRootSubweb = rootSubwebs.includes(subweb)
  const subwebPath = isRootSubweb ? '' : `/${subweb}`
  const homePath = `/${lang}${subwebPath}/`

  let pagePath = slug || ''
  if (withRootHome && pagePath === '' && isRootSubweb) {
    pagePath = rootSubwebsHomeSlug[subweb]
  }

  const path = `${homePath}${pagePath}`
  if (withDomain) return `${PORTAL_WWW}${path}`
  if (withHost) return `${PORTAL_HOST}${path}`
  return `${path}`
}

// remove image from published seo data when dynamicImage is true
const computeSeoData = (seoData) => {
  let newSeoData = { ...seoData }
  const { dynamicImage = false } = seoData
  if (dynamicImage) {
    newSeoData = { ...newSeoData }
    delete newSeoData.dynamicImage;
    delete newSeoData.image;
  }
  return newSeoData
}

export const generatePageData = (pageData) => {
  // removes SEO organism with the dynamicSEO flag
  const pageDataReduced = pageData.reduce((page, organism) => {
    if (organism.name === 'SEO' && organism.dynamicSEO) return page
    return [...page, organism]
  }, [])
  const pageDataComputed = pageDataReduced.map(organism => {
    if (organism.name === 'SEO') return computeSeoData(organism)
    return organism
  })
  return pageDataComputed
}

export const generateInitialPageData = ({ title = '' }) => [
  {
    name: 'SEO',
    title,
    description: '',
    image: '',
    keywords: []
  }
]

export const generateSubwebsWithFavoritesFirst = (subwebs = [], subwebsFavorites = []) => {
  let computed = subwebs
  if (subwebs.length === 0) return computed
  if (subwebsFavorites.length > 0) {
    computed = subwebs.sort((first, second) => {
      const isFavorite = (subwebName) => subwebsFavorites.includes(subwebName)
      const areBothFavorites = isFavorite(first.slug) && isFavorite(second.slug)
      if (areBothFavorites) {
        const indexOfFirst = subwebsFavorites.findIndex(favorite => favorite === first.slug)
        const indexOfSecond = subwebsFavorites.findIndex(favorite => favorite === second.slug)
        if (indexOfFirst > indexOfSecond) return 1
        if (indexOfFirst < indexOfSecond) return -1
      }

      if (isFavorite(first.slug) && !isFavorite(second.slug)) return -1
      if (!isFavorite(first.slug) && isFavorite(second.slug)) return 1

      return 0
    })
  }
  return computed
}

export const computeDateDistance = (date) => {
  if (!date) return null
  const lastModifiedDate = new Date(date)
  const isValidDate = isValid(lastModifiedDate)
  if (!isValidDate) return null
  const computedDateDistance = formatDistanceToNow(
    lastModifiedDate,
    { locale: pt }
  )
  return computedDateDistance
}

export const updatePageLastModified = (pageData) => {
  const pageDataUpdated = pageData.map(organism => {
    if (organism.name === 'SEO') {
      return {
        ...organism,
        lastModified: new Date().toISOString()
      }
    }
    return organism
  })
  return pageDataUpdated
}

// removes asteriscs from images
export const filterMarkdown = (markdown) => {
  const imagesWithAsteriscs = /[*]{2}(!\[([^\]]*)]\s*\(([^)"]+)( "([^)"]+)")?\))[*]{2}/g
  const markdownWithoutImagesWithAsteriscs = markdown.replace(imagesWithAsteriscs, '$1')

  const images = /(!\[([^\]]*)]\s*\(([^)"]+)( "([^)"]+)")?\))/g
  const markdownImagesWithParagraph = markdownWithoutImagesWithAsteriscs.replace(images, '\n  \n $1 \n  \n ')
  return markdownImagesWithParagraph
}

// bug fix:
// add empty strings between article when is an atomic block
// otherwise it cant be edited
export const fixRawDraft = (rawDraftObj) => {
  const placeholderBlock = {
    depth: 0,
    entityRanges: [],
    inlineStyleRanges: [],
    text: '',
    type: 'unstyled',
  }
  const { blocks } = rawDraftObj
  const shouldAddBefore = blocks[0].type === 'atomic'
  const shouldAddAfter = blocks[blocks.length - 1].type === 'atomic'
  return {
    ...rawDraftObj,
    blocks: [
      ...(shouldAddBefore ? [placeholderBlock] : []),
      ...blocks,
      ...(shouldAddAfter ? [placeholderBlock] : []),
    ]
  }
}

export const generateReadablePtDate = (date) => {
  if (!date) return 'Sem informação'
  const parsedDate = new Date(date)
  const isValidDate = isValid(parsedDate)
  if (!isValidDate) return 'Sem informação'
  const computeDate = format(parsedDate, 'dd/MM/yy')
  const hours = format(parsedDate, 'HH')
  const minutes = format(parsedDate, 'mm')
  const seconds = format(parsedDate, 'ss')
  return `${computeDate} às ${hours}h${minutes}m${seconds}s`
}

export const convertSessionPropsToFormProps = ({
  id, registrationLink, startDate, endDate
}) => ({
  id: id || '',
  registrationLink: registrationLink || '',
  date: startDate || '',
  startHour: startDate ? format(new Date(startDate), 'HH:mm') : null,
  endHour: endDate ? format(new Date(endDate), 'HH:mm') : null
})

export const convertSessionFormToProps = ({
  id,
  registrationLink,
  date,
  startHour,
  endHour,
}, courses) => {
  const [startHours, startMinutes] = startHour.split(':')
  const [endHours, endMinutes] = endHour.split(':')
  const course = courses.find(c => c.value === id)

  return {
    id,
    registrationLink,
    label: course ? course.label : '',
    startDate: set(new Date(date), { hours: startHours, minutes: startMinutes }).toISOString(),
    endDate: set(new Date(date), { hours: endHours, minutes: endMinutes }).toISOString(),
  }
}
